import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { Send } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { taskService, type TaskActivity, type TaskUpdate } from '@/lib/services/taskService'
import { useToastNotification } from '@/lib/utils/useToastNotification'

type TimelineEntry =
  | { kind: 'update'; at: string; item: TaskUpdate }
  | { kind: 'activity'; at: string; item: TaskActivity }

type Props = {
  taskId: number
  isDisabled?: boolean
}

const MAX_CHARS = 2000

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-PH', {
    timeZone: 'Asia/Manila',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

function updateAuthorName(update: TaskUpdate): string {
  return update.author?.full_name?.trim() || 'Agent'
}

function activitySummary(activity: TaskActivity): string {
  const module = activity.module_code.replace(/_/g, ' ')
  const event = activity.event_code.replace(/_/g, ' ')
  return `${module} · ${event}`
}

function activityDetail(activity: TaskActivity): string | null {
  const payload = activity.payload
  if (!payload || typeof payload !== 'object') return null
  const message = payload.message ?? payload.summary ?? payload.note
  return typeof message === 'string' && message.trim() ? message.trim() : null
}

export default function TaskTimeline({ taskId, isDisabled = false }: Props) {
  const colors = useThemeColors()
  const { showSuccess, showError } = useToastNotification()
  const [updates, setUpdates] = useState<TaskUpdate[]>([])
  const [activities, setActivities] = useState<TaskActivity[]>([])
  const [loading, setLoading] = useState(true)
  const [content, setContent] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const loadTimeline = useCallback(async () => {
    setLoading(true)
    try {
      const [updateRows, activityRows] = await Promise.all([
        taskService.listUpdates(taskId),
        taskService.listActivities(taskId),
      ])
      setUpdates(updateRows)
      setActivities(activityRows)
    } catch (err) {
      showError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [taskId, showError])

  useEffect(() => {
    void loadTimeline()
  }, [loadTimeline])

  const entries = useMemo<TimelineEntry[]>(() => {
    const merged: TimelineEntry[] = [
      ...updates.map((item) => ({ kind: 'update' as const, at: item.created_at, item })),
      ...activities.map((item) => ({ kind: 'activity' as const, at: item.created_at, item })),
    ]
    return merged.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
  }, [updates, activities])

  const handleSubmit = async () => {
    const trimmed = content.trim()
    if (!trimmed || isDisabled) return
    setSubmitting(true)
    try {
      await taskService.createUpdate(taskId, {
        content: trimmed,
        type: isInternal ? 'note' : 'comment',
        is_internal: isInternal,
      })
      showSuccess(isInternal ? 'Internal note added' : 'Update posted')
      setContent('')
      setIsInternal(false)
      await loadTimeline()
    } catch (err) {
      showError(err as Error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <VStack align="stretch" spacing={4}>
      {!isDisabled ? (
        <Box borderWidth="1px" borderRadius="md" borderColor={colors.borderLight} p={3}>
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value.slice(0, MAX_CHARS))}
            placeholder="Add a task update or internal note…"
            minH="88px"
            resize="vertical"
          />
          <Flex mt={2} align="center" justify="space-between" gap={3} flexWrap="wrap">
            <Checkbox
              size="sm"
              isChecked={isInternal}
              onChange={(e) => setIsInternal(e.target.checked)}
            >
              Internal note
            </Checkbox>
            <HStack spacing={2}>
              <Text fontSize="xs" color={colors.textSecondary}>
                {content.length}/{MAX_CHARS}
              </Text>
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<Send size={14} />}
                onClick={() => void handleSubmit()}
                isLoading={submitting}
                isDisabled={!content.trim()}
              >
                Post
              </Button>
            </HStack>
          </Flex>
        </Box>
      ) : null}

      {loading ? (
        <HStack py={6} justify="center">
          <Spinner size="sm" />
          <Text fontSize="sm" color={colors.textSecondary}>
            Loading activity…
          </Text>
        </HStack>
      ) : entries.length === 0 ? (
        <Text fontSize="sm" color={colors.textSecondary}>
          No activity yet.
        </Text>
      ) : (
        <VStack align="stretch" spacing={0} divider={<Box borderBottomWidth="1px" borderColor={colors.borderLight} />}>
          {entries.map((entry) => {
            if (entry.kind === 'update') {
              const update = entry.item
              return (
                <Box key={`update-${update.id}`} py={3}>
                  <HStack justify="space-between" align="flex-start" mb={1}>
                    <Text fontSize="sm" fontWeight="600">
                      {updateAuthorName(update)}
                      {update.is_internal ? (
                        <Text as="span" fontSize="xs" color="orange.500" ml={2}>
                          Internal
                        </Text>
                      ) : null}
                    </Text>
                    <Text fontSize="xs" color={colors.textSecondary}>
                      {formatDate(entry.at)}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" whiteSpace="pre-wrap">
                    {update.content}
                  </Text>
                </Box>
              )
            }

            const activity = entry.item
            const detail = activityDetail(activity)
            return (
              <Box key={`activity-${activity.id}`} py={3}>
                <HStack justify="space-between" align="flex-start" mb={1}>
                  <Text fontSize="sm" fontWeight="600">
                    {activity.creator_name ?? 'System'}
                  </Text>
                  <Text fontSize="xs" color={colors.textSecondary}>
                    {formatDate(entry.at)}
                  </Text>
                </HStack>
                <Text fontSize="sm" color={colors.textSecondary}>
                  {activitySummary(activity)}
                </Text>
                {detail ? (
                  <Text fontSize="sm" mt={1} whiteSpace="pre-wrap">
                    {detail}
                  </Text>
                ) : null}
              </Box>
            )
          })}
        </VStack>
      )}
    </VStack>
  )
}
